/**
 * Bridge between the device-name search and the two facet filters.
 *
 * Typing a site name into the search box finds nothing on an account that files
 * screens by folder — no screen is NAMED "Mattingley", the folder is. Rather
 * than blend folder/team names into the search (which would hide which
 * dimension produced a hit), the query is matched against the facet names and
 * offered as one-click chips.
 *
 * Picking a chip clears the search text: the query was the folder's name, not a
 * device's, so leaving it applied would filter the folder's screens down to
 * nothing and look broken.
 */
import { Folder, Users } from 'lucide-react';
import { Button } from '@staff-app/ui';
import type { FacetOption, FacetValue } from '../utils/device-facets';

interface Props {
  query: string;
  teams: FacetOption[];
  folders: FacetOption[];
  teamTotal: number;
  folderTotal: number;
  onPickTeam: (value: FacetValue) => void;
  onPickFolder: (value: FacetValue) => void;
}

function Chip({
  icon,
  option,
  onPick,
  testId,
}: {
  icon: React.ReactNode;
  option: FacetOption;
  onPick: (value: FacetValue) => void;
  testId: string;
}) {
  return (
    <Button
      variant="outline"
      size="xs"
      className="cursor-pointer gap-1.5"
      onClick={() => onPick(option.value)}
      data-testid={testId}
    >
      {icon}
      <span className="max-w-[240px] truncate" title={option.label}>
        {option.label}
      </span>
      <span className="tabular-nums text-muted-foreground">{option.count}</span>
    </Button>
  );
}

export function FacetSuggestions({
  query,
  teams,
  folders,
  teamTotal,
  folderTotal,
  onPickTeam,
  onPickFolder,
}: Props) {
  if (teams.length === 0 && folders.length === 0) return null;

  const hiddenCount = teamTotal - teams.length + (folderTotal - folders.length);

  return (
    <div
      className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
      data-testid="mdm-devices-facet-suggestions"
    >
      <span>{`"${query.trim()}" also matches:`}</span>
      {folders.map((o) => (
        <Chip
          key={`f-${o.value}`}
          icon={<Folder className="h-3.5 w-3.5" />}
          option={o}
          onPick={onPickFolder}
          testId="mdm-devices-suggest-folder"
        />
      ))}
      {teams.map((o) => (
        <Chip
          key={`t-${o.value}`}
          icon={<Users className="h-3.5 w-3.5" />}
          option={o}
          onPick={onPickTeam}
          testId="mdm-devices-suggest-team"
        />
      ))}
      {hiddenCount > 0 ? (
        <span data-testid="mdm-devices-suggest-more">{`+${hiddenCount} more — use the pickers`}</span>
      ) : null}
    </div>
  );
}
